import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Localized, useLocalization } from "@fluent/react"
import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import ChecklistIcon from '@mui/icons-material/Checklist'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import EventIcon from '@mui/icons-material/Event'
import NotesIcon from '@mui/icons-material/Notes'
import NumbersIcon from '@mui/icons-material/Numbers'
import PersonIcon from '@mui/icons-material/Person'
import ShortTextIcon from '@mui/icons-material/ShortText'
import TitleIcon from '@mui/icons-material/Title'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Box, Button, List, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material"
import { memo, ReactElement, useState } from "react"
import { useAppSelector } from "../../../app/hooks"
import { selectSettings } from "../../settings/settingsSlice"
import { FormFieldDef, FormFieldType } from "../../documents/sections/formSection/formSchema"
import { DefinitionIssue } from "./formDefinitionRules"

export const FIELD_TYPES: FormFieldType[] = ['text', 'textarea', 'number', 'date', 'select', 'multiselect', 'checkbox', 'user', 'heading']

export const FIELD_TYPE_ICONS: Record<FormFieldType, ReactElement> = {
    text: <ShortTextIcon fontSize="small" />,
    textarea: <NotesIcon fontSize="small" />,
    number: <NumbersIcon fontSize="small" />,
    date: <EventIcon fontSize="small" />,
    select: <ArrowDropDownCircleIcon fontSize="small" />,
    multiselect: <ChecklistIcon fontSize="small" />,
    checkbox: <CheckBoxIcon fontSize="small" />,
    user: <PersonIcon fontSize="small" />,
    heading: <TitleIcon fontSize="small" />,
}

/** The admin edits in the UI language, but either translation is better than an empty row. */
export const fieldCaption = (field: FormFieldDef, uiLocale: string) => {
    const preferred = uiLocale.startsWith('pl') ? [field.label.PL, field.label.EN] : [field.label.EN, field.label.PL]
    return preferred.find(l => l.trim() !== '') ?? ''
}

const OutlineRow = memo(({ field, selected, severity, readOnly, uiLocale, onSelect }: {
    field: FormFieldDef
    selected: boolean
    severity: 'error' | 'warning' | undefined
    readOnly: boolean
    uiLocale: string
    onSelect: (id: string) => void
}) => {
    const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id: field.id, disabled: readOnly })
    const caption = fieldCaption(field, uiLocale)
    const heading = field.type === 'heading'

    return <ListItemButton
        ref={setNodeRef}
        selected={selected}
        onClick={() => onSelect(field.id)}
        sx={{
            transform: CSS.Transform.toString(transform),
            transition,
            backgroundColor: isDragging ? 'action.hover' : undefined,
            borderTop: heading ? '1px solid' : undefined,
            borderColor: 'divider',
            pl: heading ? 1 : 3,
        }}
    >
        {!readOnly && <Box component="span" {...attributes} {...listeners}
            sx={{ display: 'flex', mr: 0.5, cursor: 'grab', touchAction: 'none', color: 'action.active' }}>
            <DragIndicatorIcon fontSize="small" />
        </Box>}
        <ListItemIcon sx={{ minWidth: 32 }}>{FIELD_TYPE_ICONS[field.type]}</ListItemIcon>
        <ListItemText
            primary={caption || <em>—</em>}
            primaryTypographyProps={{ noWrap: true, fontWeight: heading ? 600 : undefined }}
            secondary={field.key}
            secondaryTypographyProps={{ noWrap: true, sx: { fontFamily: 'monospace' } }}
        />
        {severity === 'error' && <ErrorOutlineIcon fontSize="small" color="error" />}
        {severity === 'warning' && <WarningAmberIcon fontSize="small" color="warning" />}
    </ListItemButton>
})

export const FieldOutline = ({ fields, selectedId, issues, readOnly, onSelect, onReorder, onAdd }: {
    fields: FormFieldDef[]
    selectedId: string | null
    issues: DefinitionIssue[]
    readOnly: boolean
    onSelect: (id: string) => void
    onReorder: (fields: FormFieldDef[]) => void
    onAdd: (type: FormFieldType) => void
}) => {
    const { l10n } = useLocalization()
    const uiLocale = useAppSelector(selectSettings).locale
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

    const severityOf = (fieldId: string) => {
        const own = issues.filter(i => i.fieldId === fieldId)
        return own.some(i => i.severity === 'error') ? 'error' : own.length > 0 ? 'warning' : undefined
    }

    const handleDragEnd = ({ active, over }: DragEndEvent) => {
        if (!over || active.id === over.id) return
        const from = fields.findIndex(f => f.id === active.id)
        const to = fields.findIndex(f => f.id === over.id)
        onReorder(arrayMove(fields, from, to))
    }

    return <Box>
        {fields.length === 0
            ? <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                <Localized id="form-def-no-fields"><span>_Formularz nie ma jeszcze pól.</span></Localized>
            </Typography>
            : <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                    <List dense disablePadding>
                        {fields.map(field => <OutlineRow
                            key={field.id}
                            field={field}
                            selected={field.id === selectedId}
                            severity={severityOf(field.id)}
                            readOnly={readOnly}
                            uiLocale={uiLocale}
                            onSelect={onSelect}
                        />)}
                    </List>
                </SortableContext>
            </DndContext>}
        {!readOnly && <Box sx={{ p: 1 }}>
            <Button startIcon={<AddIcon />} onClick={e => setMenuAnchor(e.currentTarget)}>
                <Localized id="form-def-add-field"><span>_Dodaj pole</span></Localized>
            </Button>
            <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={() => setMenuAnchor(null)}>
                {FIELD_TYPES.map(type => <MenuItem key={type} onClick={() => { setMenuAnchor(null); onAdd(type) }}>
                    <ListItemIcon>{FIELD_TYPE_ICONS[type]}</ListItemIcon>
                    <ListItemText>{l10n.getString(`form-field-type-${type}`, null, type)}</ListItemText>
                </MenuItem>)}
            </Menu>
        </Box>}
    </Box>
}
